/* eslint-disable no-case-declarations */
import { auth, db, serverTimestamp } from 'firebase/firebase';
import { collection, deleteDoc, doc, getDoc,setDoc } from 'firebase/firestore';

import { isDev } from 'constants/commons';

/**
 * @description Create a new document in the collection
 * @param {string} collectionName - The name of the collection
 * @param {string} documentId - The id of the document
 * @param {object} dataToSave - The data to save
 * @param {string} method - The method to use
 * @param {boolean} idAsMainKey - If true, the id will be the main key
 * @param {boolean} forceUpdate - If true, the document will be updated
 * @param {boolean} returnStoredData - If true, the stored data will be returned
 * @returns {Promise<object>} The document data
 */
export const createOperation = async (
  collectionName,
  documentId,
  dataToSave,
  method = 'create',
  idAsMainKey = false,
  forceUpdate = true,
  returnStoredData = false,
) => {
  try {
    if (!['create', 'update', 'delete'].includes(method.toLowerCase())) {
      throw new Error('Invalid method. Supported methods are: create, update, delete');
    }

    const collectionRef = collection(db, collectionName);

    let newDocData = dataToSave;
    let output = {};

    const auditFields = {
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      uid: auth?.currentUser?.uid,
    };

    // eslint-disable-next-line default-case
    switch (method.toLowerCase()) {
      case 'create':
        const newDocRef = idAsMainKey ? doc(collectionRef, documentId) : doc(collectionRef);
        newDocData = {
          ...dataToSave,
          id: newDocRef.id, // New Doc Id
          ...auditFields,
        };
        output = newDocData;
        await setDoc(newDocRef, newDocData);

        // Get the stored data if requested
        if (returnStoredData) {
          const docSnapshot = await getDoc(newDocRef);
          if (docSnapshot.exists()) output = { id: docSnapshot.id, ...docSnapshot.data() };
        }
        break;
      case 'update':
        if (!documentId) {
          throw new Error('Document ID is required for update operation');
        }
        const updatedAt = serverTimestamp();
        newDocData = {
          ...dataToSave,
          updatedAt,
        };
        // Keep owner `uid` unchanged (restricted after create). `updatedBy` is
        // the last writer for services `resolveDocumentAuditActorUid`.
        if (auth?.currentUser?.uid) {
          newDocData.updatedBy = auth.currentUser.uid;
        }
        output = { ...newDocData, id: documentId };
        await setDoc(doc(collectionRef, documentId), newDocData, { merge: forceUpdate });

        // Get the stored data if requested
        if (returnStoredData) {
          const docSnapshot = await getDoc(doc(collectionRef, documentId));
          if (docSnapshot.exists()) output = { id: docSnapshot.id, ...docSnapshot.data() };
        }
        break;
      case 'delete':
        if (!documentId) {
          throw new Error('Document ID is required for delete operation');
        }
        newDocData = { id: documentId };
        await deleteDoc(doc(collectionRef, documentId));
        break;
    }
    if (isDev) {
      // eslint-disable-next-line no-console
      console.info(`Successfully ${method}d data in collection: ${collectionName}`);
    }
    return Promise.resolve(output);
  } catch (error) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('Operation error', error);
    }
    return Promise.reject(error);
  }
};
